/**
 * Minimum perspective camera distance from a bounding sphere of `radius` so the
 * sphere fits inside both the vertical FOV and the horizontal FOV derived from
 * `aspect`, scaled by `margin` (>1 leaves padding). The horizontal FOV narrows
 * as `aspect` drops below 1 (portrait), so the limiting axis flips with aspect:
 * landscape is height-bound, portrait is width-bound.
 */
export function fitPerspectiveDistance(radius: number, fovDeg: number, aspect: number, margin: number): number {
  const vFov = (fovDeg * Math.PI) / 180;
  const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect);
  const distV = radius / Math.sin(vFov / 2);
  const distH = radius / Math.sin(hFov / 2);
  return Math.max(distV, distH) * margin;
}

/**
 * Orthographic zoom so a box of half-width/half-height fits the VIEW_HEIGHT-tall
 * frustum (visible width = visible height * aspect), divided by `margin` (>1
 * leaves padding). A zero half-extent imposes no constraint on that axis
 * (Infinity), so the other axis governs.
 */
export function fitOrthoZoom(
  halfW: number,
  halfH: number,
  viewHalfHeight: number,
  aspect: number,
  margin: number,
): number {
  const zoomH = halfH > 0 ? viewHalfHeight / halfH : Infinity;
  const zoomW = halfW > 0 ? (viewHalfHeight * aspect) / halfW : Infinity;
  return Math.min(zoomH, zoomW) / margin;
}
